import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { signIn } from '../utils/auth'
import { useAuth } from '../components/AuthProvider'
import NaturalBackground from '../components/NaturalBackground'
import MindPolishLogo from '../components/MindPolishLogo'

const Login = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [emailValid, setEmailValid] = useState<boolean | null>(null)

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/home')
    }
  }, [user, navigate])

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)
    if (value) {
      setEmailValid(validateEmail(value))
    } else {
      setEmailValid(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await signIn(email, password)
      setSuccess('Login successful!')
      setTimeout(() => {
        navigate('/home')
      }, 1000)
    } catch (err: any) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <NaturalBackground variant="sky">
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="mx-auto mb-6">
                <MindPolishLogo size="lg" className="justify-center" />
              </div>
              <h1 className="text-cream-600 font-light text-2xl mb-2">Welcome Back</h1>
              <p className="text-cream-500 font-light text-sm">
                Sign in to access your peaceful space
              </p>
            </div>

            {success && (
              <div className="p-4 bg-mint-50/80 border border-mint-100/50 rounded-2xl mb-6">
                <p className="text-mint-600 text-sm font-light text-center">{success}</p>
              </div>
            )}

            {error && (
              <div className="p-4 bg-peach-50/60 border border-peach-200/50 rounded-2xl mb-6">
                <p className="text-peach-600 text-sm font-light text-center">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="bg-cream-50/80 backdrop-blur-sm rounded-3xl border border-cream-100/50 p-6 sm:p-8 shadow-lg space-y-6">
              <div>
                <label htmlFor="email" className="block text-cream-600 font-light text-sm mb-2">
                  Email
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={handleEmailChange}
                    className={`w-full p-4 rounded-2xl border ${
                      emailValid === true ? 'border-mint-200/50 focus:border-mint-300' : 
                      emailValid === false ? 'border-peach-200/50 focus:border-peach-300' : 
                      'border-cream-200/50 focus:border-cream-300'
                    } focus:ring-2 focus:ring-cream-100/50 focus:outline-none font-light text-sm bg-white/50`}
                    required
                    aria-invalid={emailValid === false}
                    aria-describedby={emailValid === false ? "email-error" : undefined}
                  />
                  {emailValid !== null && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      {emailValid ? (
                        <svg className="w-4 h-4 text-mint-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-peach-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
                {emailValid === false && (
                  <p id="email-error" className="mt-1 text-peach-600 text-xs">
                    Please enter a valid email address
                  </p>
                )}
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-cream-600 font-light text-sm">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-cream-500 text-xs font-light hover:text-cream-600 transition-colors">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-4 pr-12 rounded-2xl border border-cream-200/50 focus:border-cream-300 focus:ring-2 focus:ring-cream-100/50 focus:outline-none font-light text-sm bg-white/50"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-500 focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-4 bg-cream-100/80 hover:bg-cream-200/60 
                           text-cream-600 rounded-2xl text-base font-light border border-cream-200/50 hover:border-cream-300/60
                           transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-cream-100
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="text-center mt-8">
              <p className="text-cream-500 font-light text-sm mb-4">
                Don't have an account yet?
              </p>
              <Link
                to="/signup"
                className="px-6 py-3 bg-mint-50/60 hover:bg-mint-100/50 
                           text-mint-600 rounded-2xl text-sm font-light border border-mint-100/40 hover:border-mint-200/50
                           transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-mint-100/50 inline-block"
              >
                Create Account
              </Link>
            </div>

            <div className="text-center mt-6">
              <button
                onClick={() => navigate('/home')}
                className="text-cream-400 text-sm font-light hover:text-cream-500 transition-colors"
              >
                Continue without account
              </button>
            </div>
          </div>
        </div>
      </div>
    </NaturalBackground>
  )
}

export default Login